import { useMemo, useState } from 'react';
import { AlertTriangle, Check, ChevronDown, Clock, Crosshair, RotateCcw, Search, Server, SlidersHorizontal, X } from 'lucide-react';
import { cn } from '../../utils/cn';
import { LOGBOOK_OUTCOMES } from '../../types/logbook';
import {
  LOGBOOK_ACTION_LIMIT, LOGBOOK_PRESETS, LOGBOOK_WINDOWS, actionMeta, actionsByCategory, dimensionLabel, outcomeMeta,
} from '../../utils/logbook';
import { activeFilterCount, type LogbookFilterState } from '../../utils/logbookFilters';
import { parseCoordinates } from '../../utils/coords';
import type { LogbookServerView } from '../../hooks/useLogbookServers';

interface LogbookFiltersProps {
  value: LogbookFilterState;
  onChange: (next: LogbookFilterState) => void;
  onReset: () => void;
  servers: LogbookServerView[];
  clusters: string[];
  disabled?: boolean;
}

const DIMENSIONS = ['minecraft:overworld', 'minecraft:the_nether', 'minecraft:the_end'];

/**
 * The query builder.
 *
 * One visible row answers the common question (who, when, what kind of action), and everything else
 * is one click away. That split is deliberate: a filter panel that shows fourteen inputs at once
 * reads as work, and most investigations start from a name and a time.
 */
export const LogbookFilters = ({ value, onChange, onReset, servers, clusters, disabled = false }: LogbookFiltersProps) => {
  const [advanced, setAdvanced] = useState(false);
  const [actionPickerOpen, setActionPickerOpen] = useState(false);
  const [serverPickerOpen, setServerPickerOpen] = useState(false);
  const [actionQuery, setActionQuery] = useState('');
  const [coordinatePaste, setCoordinatePaste] = useState('');
  const [radius, setRadius] = useState(16);

  const patch = (changes: Partial<LogbookFilterState>) => onChange({ ...value, ...changes });
  const filters = activeFilterCount(value);
  const categories = useMemo(actionsByCategory, []);

  const atLimit = value.actions.length >= LOGBOOK_ACTION_LIMIT;

  const toggleAction = (action: string) => {
    const next = value.actions.includes(action)
      ? value.actions.filter((entry) => entry !== action)
      : [...value.actions, action].slice(0, LOGBOOK_ACTION_LIMIT);
    patch({ actions: next });
  };

  /** Categories filtered by the search box, each sub-grouped by the integration that captured it. */
  const matchingCategories = useMemo(() => {
    const needle = actionQuery.trim().toLowerCase();
    return categories
      .map(({ category, actions }) => {
        const matching = needle
          ? actions.filter((meta) => `${meta.label} ${meta.action} ${meta.source}`.toLowerCase().includes(needle))
          : actions;
        const sources = new Map<string, typeof matching>();
        for (const meta of matching) {
          const bucket = sources.get(meta.source);
          if (bucket) bucket.push(meta);
          else sources.set(meta.source, [meta]);
        }
        return {
          category,
          total: matching.length,
          bySource: Array.from(sources, ([source, entries]) => ({ source, actions: entries })),
        };
      })
      .filter((group) => group.total > 0);
  }, [categories, actionQuery]);

  const toggleServer = (serverId: string) => {
    const next = value.servers.includes(serverId)
      ? value.servers.filter((entry) => entry !== serverId)
      : [...value.servers, serverId];
    patch({ servers: next });
  };

  const applyPreset = (actions: string[]) => {
    const alreadyExact = actions.length === value.actions.length && actions.every((action) => value.actions.includes(action));
    patch({ actions: alreadyExact ? [] : actions });
  };

  const applyCoordinatePaste = () => {
    const parsed = parseCoordinates(coordinatePaste);
    if (!parsed) return;
    const span = Math.max(1, Math.min(2048, radius));
    patch({
      box: [
        parsed.x - span, parsed.y - span, parsed.z - span,
        parsed.x + span, parsed.y + span, parsed.z + span,
      ],
    });
    setCoordinatePaste('');
  };

  const serverLabel = value.servers.length === 0
    ? 'All authorized servers'
    : value.servers.length === 1
      ? (servers.find((server) => server.serverId === value.servers[0])?.pack?.title ?? value.servers[0])
      : `${value.servers.length} servers`;

  const actionLabel = value.actions.length === 0
    ? 'Every action'
    : value.actions.length === 1
      ? actionMeta(value.actions[0]).label
      : `${value.actions.length} actions`;

  return (
    <div className="glass-card p-3 md:p-4 space-y-3" data-tour="logbook-filters">
      {/* Primary row: who, when, where, and how far to open up */}
      <div className="flex flex-col md:flex-row md:items-center gap-2" data-tour="logbook-filter-row">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
          <input
            type="text"
            value={value.actorName}
            onChange={(event) => patch({ actorName: event.target.value })}
            placeholder="Player name"
            className="form-input pl-9"
            disabled={disabled}
            aria-label="Player name"
          />
        </div>

        <div className="relative">
          <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
          <select
            value={value.windowId}
            onChange={(event) => patch({ windowId: event.target.value })}
            className="glass-select pl-9 w-full md:w-48"
            disabled={disabled}
            aria-label="Time window"
          >
            {LOGBOOK_WINDOWS.map((window) => <option key={window.id} value={window.id}>{window.label}</option>)}
            {value.windowId === 'custom' && <option value="custom">Selected range</option>}
          </select>
        </div>

        <button
          type="button"
          onClick={() => { setServerPickerOpen((open) => !open); setActionPickerOpen(false); }}
          className={cn('glass-button justify-between md:w-56', value.servers.length > 0 && 'border-accent-400/40')}
          disabled={disabled}
          aria-expanded={serverPickerOpen}
        >
          <span className="flex items-center gap-2 min-w-0">
            <Server className="w-4 h-4 shrink-0" />
            <span className="truncate">{serverLabel}</span>
          </span>
          <ChevronDown className={cn('w-4 h-4 shrink-0 transition-transform', serverPickerOpen && 'rotate-180')} />
        </button>

        <button
          type="button"
          onClick={() => { setActionPickerOpen((open) => !open); setServerPickerOpen(false); }}
          className={cn('glass-button justify-between md:w-52', value.actions.length > 0 && 'border-accent-400/40')}
          disabled={disabled}
          aria-expanded={actionPickerOpen}
        >
          <span className="flex items-center gap-2 min-w-0">
            <SlidersHorizontal className="w-4 h-4 shrink-0" />
            <span className="truncate">{actionLabel}</span>
          </span>
          <ChevronDown className={cn('w-4 h-4 shrink-0 transition-transform', actionPickerOpen && 'rotate-180')} />
        </button>

        {filters > 0 && (
          <button type="button" onClick={onReset} className="btn-ghost" title="Clear every filter" aria-label="Clear every filter">
            <RotateCcw className="w-4 h-4" />
            <span className="md:hidden">Clear filters</span>
          </button>
        )}
      </div>

      {/* Question presets: the actual reason someone opens this page */}
      <div className="flex flex-wrap items-center gap-1.5" data-tour="logbook-presets">
        <span className="eyebrow mr-1">Looking for</span>
        {LOGBOOK_PRESETS.map((preset) => {
          const Icon = preset.icon;
          const active = preset.actions.length === value.actions.length
            && preset.actions.every((action) => value.actions.includes(action));
          return (
            <button
              key={preset.id}
              type="button"
              onClick={() => applyPreset(preset.actions)}
              className={cn('chip gap-1.5', active && 'border-accent-400/50 bg-accent-500/15')}
              title={preset.description}
              disabled={disabled}
            >
              <Icon className="w-3.5 h-3.5" />
              {preset.label}
            </button>
          );
        })}
      </div>

      {serverPickerOpen && (
        <div className="subcard p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="eyebrow">Servers</span>
            <div className="flex items-center gap-2">
              {clusters.length > 0 && (
                <select
                  value={value.cluster}
                  onChange={(event) => patch({ cluster: event.target.value })}
                  className="glass-select text-xs"
                  aria-label="Cluster"
                >
                  <option value="">Any cluster</option>
                  {clusters.map((cluster) => <option key={cluster} value={cluster}>{cluster}</option>)}
                </select>
              )}
              {value.servers.length > 0 && (
                <button type="button" className="btn-ghost btn-xs" onClick={() => patch({ servers: [] })}>Clear</button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1 max-h-64 overflow-y-auto">
            {servers.map((server) => {
              const selected = value.servers.includes(server.serverId);
              return (
                <button
                  key={server.serverId}
                  type="button"
                  onClick={() => toggleServer(server.serverId)}
                  className={cn(
                    'flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-sm min-w-0 transition-colors',
                    selected ? 'bg-accent-500/15 text-white' : 'hover:bg-glass-200/[0.06] text-glass-300',
                  )}
                >
                  <span className={cn('w-3.5 h-3.5 rounded-[4px] border shrink-0 flex items-center justify-center',
                    selected ? 'bg-accent-500/80 border-accent-400' : 'border-glass-200/25')}
                  >
                    {selected && <Check className="w-2.5 h-2.5 text-on-solid" />}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block truncate">{server.pack?.title ?? server.serverId}</span>
                    <span className="block truncate text-[11px] text-glass-500 font-mono">{server.serverId}</span>
                  </span>
                  {server.pack?.online && <span className="w-1.5 h-1.5 rounded-full bg-green-400 shrink-0" aria-label="Online" />}
                </button>
              );
            })}
            {servers.length === 0 && <p className="text-xs text-glass-500 col-span-full">No servers are configured for this credential.</p>}
          </div>
        </div>
      )}

      {actionPickerOpen && (
        <div className="subcard p-3 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="eyebrow">
              Actions
              <span className={cn('ml-2 tabular-nums', atLimit ? 'text-yellow-300' : 'text-glass-500')}>
                {value.actions.length} of {LOGBOOK_ACTION_LIMIT}
              </span>
            </span>
            {value.actions.length > 0 && (
              <button type="button" className="btn-ghost btn-xs" onClick={() => patch({ actions: [] })}>Clear all</button>
            )}
          </div>

          {/* Selected first, so removing one never means hunting for it in the list. */}
          {value.actions.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {value.actions.map((action) => {
                const meta = actionMeta(action);
                return (
                  <button
                    key={action}
                    type="button"
                    onClick={() => toggleAction(action)}
                    className="chip chip-removable gap-1.5 border-accent-400/50 bg-accent-500/15 text-white"
                    title={`Remove ${action}`}
                  >
                    {meta.source}: {meta.label}
                    <X className="w-3 h-3" />
                  </button>
                );
              })}
            </div>
          )}

          {atLimit && (
            <div className="alert alert-warning">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>At most {LOGBOOK_ACTION_LIMIT} actions in one search.</span>
            </div>
          )}

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
            <input
              type="text"
              value={actionQuery}
              onChange={(event) => setActionQuery(event.target.value)}
              placeholder="Find an action or a mod"
              className="form-input pl-9"
              aria-label="Find an action"
            />
          </div>

          <div className="max-h-80 overflow-y-auto space-y-3">
            {matchingCategories.length === 0 && (
              <p className="text-sm text-glass-500">Nothing matches that.</p>
            )}
            {matchingCategories.map(({ category, bySource, total }) => (
              <div key={category}>
                <div className="eyebrow mb-1.5 flex items-center gap-2">
                  {category}
                  <span className="text-glass-600 tabular-nums">{total}</span>
                </div>
                {/* A category with many integrations is grouped by the mod that captured it, because
                    fifty chips all reading "Block break" would say nothing about which is which. */}
                {bySource.length > 1 && total > 10 ? (
                  <div className="space-y-1">
                    {bySource.map(({ source, actions }) => (
                      <div key={source} className="flex flex-wrap items-center gap-1.5">
                        <span className="text-xs text-glass-400 w-44 shrink-0 truncate" title={source}>{source}</span>
                        {actions.map((meta) => {
                          const Icon = meta.icon;
                          const selected = value.actions.includes(meta.action);
                          return (
                            <button
                              key={meta.action}
                              type="button"
                              onClick={() => toggleAction(meta.action)}
                              disabled={!selected && atLimit}
                              className={cn('chip gap-1.5', selected && 'border-accent-400/50 bg-accent-500/15 text-white')}
                              title={`${meta.action}${meta.caveat ? `. ${meta.caveat}` : ''}`}
                            >
                              <Icon className="w-3.5 h-3.5" />
                              {meta.label}
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {bySource.flatMap(({ actions }) => actions).map((meta) => {
                      const Icon = meta.icon;
                      const selected = value.actions.includes(meta.action);
                      return (
                        <button
                          key={meta.action}
                          type="button"
                          onClick={() => toggleAction(meta.action)}
                          disabled={!selected && atLimit}
                          className={cn('chip gap-1.5', selected && 'border-accent-400/50 bg-accent-500/15 text-white')}
                          title={`${meta.action} via ${meta.source}${meta.caveat ? `. ${meta.caveat}` : ''}`}
                        >
                          <Icon className="w-3.5 h-3.5" />
                          {meta.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Everything else */}
      <div>
        <button
          type="button"
          onClick={() => setAdvanced((open) => !open)}
          className="btn-ghost btn-xs"
          aria-expanded={advanced}
          data-tour="logbook-more-filters"
        >
          <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', advanced && 'rotate-180')} />
          {advanced ? 'Fewer filters' : 'More filters'}
          {!advanced && filters > 0 && <span className="badge badge-accent ml-1">{filters}</span>}
        </button>

        {advanced && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <label className="block min-w-0">
              <span className="block text-xs font-medium text-glass-400 mb-1.5">Player UUID</span>
              <input
                type="text"
                value={value.actorId}
                onChange={(event) => patch({ actorId: event.target.value })}
                placeholder="Player or machine UUID"
                className="form-input font-mono text-xs"
                disabled={disabled}
              />
            </label>

            <label className="block min-w-0">
              <span className="block text-xs font-medium text-glass-400 mb-1.5">Outcome</span>
              <select
                value={value.outcome}
                onChange={(event) => patch({ outcome: event.target.value as LogbookFilterState['outcome'] })}
                className="glass-select w-full"
                disabled={disabled}
              >
                <option value="">Any outcome</option>
                {LOGBOOK_OUTCOMES.map((outcome) => (
                  <option key={outcome} value={outcome}>{outcomeMeta(outcome).label}</option>
                ))}
              </select>
              {value.outcome && <span className="block text-xs text-glass-500 mt-1">{outcomeMeta(value.outcome).meaning}</span>}
            </label>

            <label className="block min-w-0">
              <span className="block text-xs font-medium text-glass-400 mb-1.5">Dimension</span>
              <select
                value={value.dimension}
                onChange={(event) => patch({ dimension: event.target.value })}
                className="glass-select w-full"
                disabled={disabled}
              >
                <option value="">Any dimension</option>
                {DIMENSIONS.map((dimension) => <option key={dimension} value={dimension}>{dimensionLabel(dimension)}</option>)}
                {value.dimension && !DIMENSIONS.includes(value.dimension) && (
                  <option value={value.dimension}>{dimensionLabel(value.dimension)}</option>
                )}
              </select>
            </label>

            <label className="block min-w-0">
              <span className="block text-xs font-medium text-glass-400 mb-1.5">World id</span>
              <input
                type="text"
                value={value.worldId}
                onChange={(event) => patch({ worldId: event.target.value })}
                placeholder="Any world"
                className="form-input font-mono text-xs"
                disabled={disabled}
              />
            </label>

            <label className="block min-w-0">
              <span className="block text-xs font-medium text-glass-400 mb-1.5">Object id</span>
              <input
                type="text"
                value={value.objectId}
                onChange={(event) => patch({ objectId: event.target.value })}
                placeholder="minecraft:diamond_block"
                className="form-input font-mono text-xs"
                disabled={disabled}
              />
            </label>

            <label className="block min-w-0">
              <span className="block text-xs font-medium text-glass-400 mb-1.5">Mod namespace</span>
              <input
                type="text"
                value={value.namespace}
                onChange={(event) => patch({ namespace: event.target.value })}
                placeholder="create, ae2, cobblemon"
                className="form-input font-mono text-xs"
                disabled={disabled}
              />
            </label>

            <label className="block min-w-0">
              <span className="block text-xs font-medium text-glass-400 mb-1.5">Page size</span>
              <select
                value={value.limit}
                onChange={(event) => patch({ limit: Number(event.target.value) })}
                className="glass-select w-full"
                disabled={disabled}
              >
                {[25, 50, 100, 200].map((size) => <option key={size} value={size}>{size} per page</option>)}
              </select>
            </label>

            {/* Region: the filter that turns "my base was griefed" into an answer */}
            <div className="min-w-0 md:col-span-2 lg:col-span-4">
              <span className="block text-xs font-medium text-glass-400 mb-1.5">Region</span>
              {value.box ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="chip font-mono text-xs gap-1.5">
                    <Crosshair className="w-3.5 h-3.5" />
                    {value.box.slice(0, 3).map(Math.round).join(', ')} to {value.box.slice(3).map(Math.round).join(', ')}
                  </span>
                  <button type="button" className="btn-ghost btn-xs" onClick={() => patch({ box: null })}>
                    <X className="w-3.5 h-3.5" />
                    Remove region
                  </button>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    value={coordinatePaste}
                    onChange={(event) => setCoordinatePaste(event.target.value)}
                    onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); applyCoordinatePaste(); } }}
                    placeholder="x y z"
                    className="form-input flex-1 font-mono text-xs"
                    disabled={disabled}
                    aria-label="Coordinates"
                  />
                  <select
                    value={radius}
                    onChange={(event) => setRadius(Number(event.target.value))}
                    className="glass-select sm:w-40"
                    aria-label="Region size"
                  >
                    {[8, 16, 32, 64, 128].map((size) => <option key={size} value={size}>&plusmn;{size} blocks</option>)}
                  </select>
                  <button type="button" className="glass-button" onClick={applyCoordinatePaste} disabled={disabled || !coordinatePaste.trim()}>
                    <Crosshair className="w-4 h-4" />
                    Set region
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
